/**
 * The iPad app (installed PWA, /m). Shared device: a mechanic unlocks with their PIN, works,
 * and the app locks after the idle time. Works offline once a mechanic has signed in online on
 * this iPad at least once.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { kvGet, kvSet } from './db';
import { ClockCard } from './ClockSheet';
import { JobScreen } from './JobScreen';
import { ChangePin, LockScreen } from './LockScreen';
import { RequestScreen } from './RequestScreen';
import { clearProblem, flush, needsSignIn, onQueueChange, problems as listProblems, queueCount } from './queue';
import type { DeviceInfo, JobsPayload } from './types';
import { Big } from './ui';
import { canUnlockOffline, rememberPin, unlockOffline } from './verifier';
import type { Problem } from './db';

type Unlocked = { userId: number; name: string; online: boolean; mustChange: boolean };

async function getJSON<T>(url: string): Promise<{ ok: true; data: T } | { ok: false; status: number }> {
  try {
    const r = await fetch(url, { credentials: 'same-origin' });
    if (!r.ok) return { ok: false, status: r.status };
    return { ok: true, data: (await r.json()) as T };
  } catch {
    return { ok: false, status: 0 }; // no network
  }
}

async function postJSON(url: string, body: unknown) {
  try {
    const r = await fetch(url, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
    return { status: r.status, body: await r.json().catch(() => ({})) };
  } catch {
    return { status: 0, body: {} };
  }
}

export function MechanicApp() {
  const [info, setInfo] = useState<DeviceInfo | null>(null);
  const [notEnrolled, setNotEnrolled] = useState(false);
  const [user, setUser] = useState<Unlocked | null>(null);
  const [payload, setPayload] = useState<JobsPayload | null>(null);
  const [openJob, setOpenJob] = useState<number | null>(null);
  const [waiting, setWaiting] = useState(0);
  const [probs, setProbs] = useState<Problem[]>([]);
  const [online, setOnline] = useState(navigator.onLine);
  const lastActivity = useRef(Date.now());

  // ---- device info (cached for offline) ----
  useEffect(() => {
    void (async () => {
      const r = await getJSON<DeviceInfo>('/api/device/me');
      if (r.ok) {
        setInfo(r.data);
        await kvSet('device', r.data);
      } else if (r.status === 401) {
        setNotEnrolled(true);
      } else {
        const cached = await kvGet<DeviceInfo>('device');
        if (cached) setInfo(cached);
        else setNotEnrolled(true);
      }
    })();
  }, []);

  // ---- queue status ----
  const refreshQueue = useCallback(async () => {
    setWaiting(await queueCount());
    setProbs(await listProblems());
  }, []);
  useEffect(() => {
    void refreshQueue();
    return onQueueChange(() => void refreshQueue());
  }, [refreshQueue]);
  useEffect(() => {
    const on = () => { setOnline(true); void flush(); };
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // ---- jobs: server when possible, cache otherwise ----
  const loadJobs = useCallback(async (u: Unlocked) => {
    if (u.online) {
      await flush();
      const r = await getJSON<JobsPayload>('/api/mechanic/jobs');
      if (r.ok) {
        setPayload(r.data);
        await kvSet(`jobs:${u.userId}`, r.data);
        return;
      }
      if (r.status === 401) {
        setUser(null); // session ended (idle on the server) — sign in again
        return;
      }
    }
    setPayload((await kvGet<JobsPayload>(`jobs:${u.userId}`)) ?? null);
  }, []);

  useEffect(() => {
    if (!user || user.mustChange) return;
    void loadJobs(user);
    const t = setInterval(() => { if (navigator.onLine && !document.hidden) void loadJobs(user); }, 60_000);
    return () => clearInterval(t);
  }, [user, loadJobs]);

  const onLocal = useCallback((fn: (p: JobsPayload) => JobsPayload) => {
    setPayload((p) => {
      if (!p) return p;
      const next = fn(p);
      void kvSet(`jobs:${next.me.id}`, next);
      return next;
    });
  }, []);

  // ---- idle lock ----
  const lock = useCallback(async () => {
    setUser(null);
    setPayload(null);
    setOpenJob(null);
    await postJSON('/auth/lock', {});
  }, []);
  useEffect(() => {
    const bump = () => (lastActivity.current = Date.now());
    const events = ['pointerdown', 'keydown', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, bump));
    const t = setInterval(() => {
      if (user && info && Date.now() - lastActivity.current > info.idleLockMinutes * 60_000) void lock();
    }, 15_000);
    return () => { events.forEach((e) => window.removeEventListener(e, bump)); clearInterval(t); };
  }, [user, info, lock]);

  // ---- unlock ----
  const unlock = async (userId: number, pin: string): Promise<string | null> => {
    const name = info?.mechanics.find((m) => m.id === userId)?.name ?? '';
    lastActivity.current = Date.now();
    const r = await postJSON('/auth/pin', { userId, pin });
    if (r.status === 200) {
      await rememberPin(userId, pin);
      setUser({ userId, name, online: true, mustChange: Boolean(r.body.mustChangePin) });
      return null;
    }
    if (r.status !== 0) {
      const left = r.body?.details?.attemptsLeft;
      return r.body?.message ? `${r.body.message}${left !== undefined ? ` (${left} tries left)` : ''}` : 'Could not sign in';
    }
    // No signal: unlock with the PIN check stored on this iPad.
    if (!(await canUnlockOffline(userId))) return "No signal, and you haven't signed in on this iPad before. Try again where there's Wi-Fi.";
    const res = await unlockOffline(userId, pin);
    if (res === 'ok') {
      setUser({ userId, name, online: false, mustChange: false });
      return null;
    }
    return res === 'locked' ? 'Too many wrong PINs. Locked for 15 minutes.' : 'Wrong PIN';
  };

  // ---- screens ----
  if (notEnrolled) return <RequestScreen />;
  if (!info) return <p className="p-8 text-xl">Loading…</p>;

  const banner = (
    <div className={`sticky top-0 z-40 flex flex-wrap items-center gap-3 px-4 py-3 text-lg ${online ? 'bg-slate-800 text-white' : 'bg-amber-400 text-black'}`}>
      <span className="font-bold">{user?.name ?? info.device.name}</span>
      {!online && <span className="font-bold">OFFLINE</span>}
      {waiting > 0 && <span>{waiting} waiting to send</span>}
      {needsSignIn && <span className="font-bold">Sign in again to send</span>}
      {user && <Big tone="light" className="ml-auto min-h-[56px]" onClick={lock}>Sign out</Big>}
    </div>
  );

  if (!user) return (<>{banner}<LockScreen info={info} onUnlock={unlock} /></>);

  if (user.mustChange) {
    return (
      <>
        {banner}
        <ChangePin length={info.pinLength} onDone={async (current, next) => {
          const r = await postJSON('/auth/pin/change', { currentPin: current, newPin: next });
          if (r.status !== 200) return r.body?.message ?? 'Could not change PIN';
          await rememberPin(user.userId, next);
          setUser({ ...user, mustChange: false });
          return null;
        }} />
      </>
    );
  }

  const job = payload?.jobs.find((j) => j.id === openJob);
  return (
    <div className="min-h-screen bg-slate-100">
      {banner}
      {probs.filter((p) => p.userId === user.userId).map((p) => (
        <div key={p.clientId} className="mx-4 mt-3 flex items-center gap-3 rounded-xl bg-red-100 p-3 text-lg text-red-900">
          <span className="flex-1"><b>{p.label}</b> wasn’t accepted: {p.message}</span>
          <Big className="min-h-[56px]" onClick={() => clearProblem(p.clientId)}>OK</Big>
        </div>
      ))}
      {!payload ? (
        <p className="p-8 text-xl">{online ? 'Loading your jobs…' : 'No saved jobs on this iPad yet — connect to Wi-Fi once.'}</p>
      ) : job ? (
        <JobScreen job={job} payload={payload} userId={user.userId} onBack={() => setOpenJob(null)} onLocal={onLocal} />
      ) : (
        <div className="mx-auto max-w-3xl p-4">
          <h1 className="mb-1 text-3xl font-black">My Jobs</h1>
          <ClockCard payload={payload} userId={user.userId} onLocal={onLocal} />
          {payload.jobs.length === 0 && <p className="text-xl text-slate-600">No jobs assigned to you right now.</p>}
          <ul className="space-y-3">
            {payload.jobs.map((j) => (
              <li key={j.id}>
                <button className="w-full rounded-2xl bg-white p-4 text-left shadow active:bg-slate-50" onClick={() => setOpenJob(j.id)}>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-black">#{j.number}</span>
                    <span className="text-lg font-semibold">
                      {payload.activePunch?.workOrderId === j.id ? '● Clocked in' : j.status.replace(/_/g, ' ').toLowerCase()}
                    </span>
                  </div>
                  <div className="text-xl font-bold">{j.customer.name}</div>
                  <div className="text-lg text-slate-700">{j.machine.make} {j.machine.model} · {j.jobType}</div>
                  {j.holdReason && <div className="text-lg font-bold text-orange-800">On hold: {j.holdReason}</div>}
                  {j.isLead === false && <div className="text-lg text-slate-600">Helping {j.lead}</div>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
